// SP-2 WS-7 — the six-step monthly-cycle stepper, modeled on
// EscrowLifecycleIndicator (numbered circles → check when done, per-step
// Timestamp) with a distinct error-tone visual for blocked steps. The step
// states are server-derived (MonthlyCycleStepDto) — this component never
// re-derives lifecycle facts.

import { AlertTriangle, Check } from "lucide-react";
import { Timestamp } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { cn } from "@/design-system/lib/utils";
import type { MonthlyCycleStepDto } from "@/api/portfolioAdmin/loanStatementsApi";
import { CYCLE_STEP_LABEL } from "./cycleCopy";

export function CycleStepper({ steps }: { steps: MonthlyCycleStepDto[] }) {
  const fmt = useDateFormatter();

  return (
    <ol
      className="flex items-start gap-space-2 overflow-x-auto list-none p-0 m-0"
      aria-label="Monthly cycle"
      data-testid="cycle-stepper"
    >
      {steps.map((step, idx) => {
        const isDone = step.state === "done";
        const isActive = step.state === "active";
        const isBlocked = step.state === "blocked";
        const next = steps[idx + 1];
        return (
          <li
            key={step.key}
            className="flex flex-col items-center text-center gap-space-1 min-w-[96px] shrink-0"
            data-step={step.key}
            data-state={step.state}
            data-testid={`cycle-step-${step.key}`}
          >
            <div className="flex items-center w-full">
              <span
                className={cn(
                  "h-px flex-1",
                  idx === 0
                    ? "opacity-0"
                    : isDone || isActive || isBlocked
                      ? "bg-status-success"
                      : "bg-border-light",
                )}
                aria-hidden="true"
              />
              <span
                className={cn(
                  "inline-flex items-center justify-center w-7 h-7 rounded-full border text-caption font-semibold shrink-0",
                  isDone && "border-status-success bg-status-success/15 text-status-success",
                  isActive && "border-gold bg-gold text-onyx animate-pulse",
                  isBlocked && "border-status-error bg-status-error/12 text-status-error",
                  step.state === "upcoming" &&
                    "border-border-light bg-white-warm text-gray-mid",
                )}
                aria-current={isActive || isBlocked ? "step" : undefined}
              >
                {isDone ? (
                  <Check size={14} aria-hidden />
                ) : isBlocked ? (
                  <AlertTriangle size={14} aria-hidden />
                ) : (
                  idx + 1
                )}
              </span>
              <span
                className={cn(
                  "h-px flex-1",
                  idx === steps.length - 1
                    ? "opacity-0"
                    : next && next.state === "done"
                      ? "bg-status-success"
                      : "bg-border-light",
                )}
                aria-hidden="true"
              />
            </div>
            <span
              className={cn(
                "text-caption leading-tight px-space-1",
                isBlocked
                  ? "text-status-error font-semibold"
                  : isActive
                    ? "text-onyx font-semibold"
                    : isDone
                      ? "text-onyx"
                      : "text-gray-mid",
              )}
            >
              {CYCLE_STEP_LABEL[step.key]}
            </span>
            {step.at ? (
              <Timestamp value={step.at} tz={fmt.tz} className="text-caption" />
            ) : (
              <span className="text-caption text-gray-mid" aria-hidden="true">
                &nbsp;
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
